"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LedgerLiveEthereumSigner = void 0;
const ethers_1 = require("ethers");
const wallet_api_client_1 = require("@ledgerhq/wallet-api-client");
const constants_1 = require("@ethersproject/constants");
const hex_1 = require("./hex");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
class AccountNotFoundError extends Error {
    constructor() {
        super("Account not found. Please use `requestAccount` method or set the signer account with `setAccount` method.");
    }
}
/**
 * Ethereum signer extended from `ethers` Signer class. The main purpose of it
 * is to allow the user to communicate with eth contracts through our tBTC SDK
 * inside Ledger Live application, when the app is used there as a Live App.
 */
class LedgerLiveEthereumSigner extends ethers_1.Signer {
    constructor(provider) {
        super();
        ethers_1.ethers.utils.defineReadOnly(this, "provider", provider);
        this._windowMessageTransport = getWindowMessageTransport();
        this._walletApiClient = getWalletAPIClient(this._windowMessageTransport);
    }
    _checkAccount() {
        if (!this._account || !this._account.id) {
            throw new AccountNotFoundError();
        }
    }
    _checkProviderAndAccount() {
        this._checkProvider();
        this._checkAccount();
    }
    _catchWalletApiError(error, defaultErrorMessage) {
        this._windowMessageTransport.disconnect();
        if (typeof error === "string" || error instanceof Error) {
            throw new Error(error.toString());
        }
        throw new Error(defaultErrorMessage ||
            "Something went wrong when using ledger live singer to interact with out wallet.");
    }
    get account() {
        return this._account;
    }
    setAccount(account) {
        this._account = account;
    }
    async requestAccount(params) {
        let account;
        try {
            this._windowMessageTransport.connect();
            account = await this._walletApiClient.account.request(params);
            this._windowMessageTransport.disconnect();
        }
        catch (err) {
            this._catchWalletApiError(err, "Something went wrong when requesting an account with ledger live signer!");
        }
        this._account = account;
        return this._account;
    }
    getAccountId() {
        this._checkAccount();
        return this._account.id;
    }
    async getAddress() {
        this._checkAccount();
        return this._account.address;
    }
    _getWalletApiEthereumTransaction(transaction) {
        const { value, to, nonce, data, gasPrice, gasLimit, maxFeePerGas, maxPriorityFeePerGas, } = transaction;
        const ethereumTransaction = {
            family: "ethereum",
            amount: value ? new bignumber_js_1.default(value.toString()) : new bignumber_js_1.default(0),
            recipient: to ? to : constants_1.AddressZero,
        };
        if (nonce)
            ethereumTransaction.nonce = Number(nonce);
        if (data)
            ethereumTransaction.data = Buffer.from(hex_1.Hex.from(data.toString()).toString(), "hex");
        if (gasPrice)
            ethereumTransaction.gasPrice = new bignumber_js_1.default(gasPrice.toString());
        if (gasLimit)
            ethereumTransaction.gasLimit = new bignumber_js_1.default(gasLimit.toString());
        if (maxFeePerGas)
            ethereumTransaction.maxFeePerGas = new bignumber_js_1.default(maxFeePerGas.toString());
        if (maxPriorityFeePerGas)
            ethereumTransaction.maxPriorityFeePerGas = new bignumber_js_1.default(maxPriorityFeePerGas.toString());
        return ethereumTransaction;
    }
    async signMessage(message) {
        this._checkAccount();
        let buffer;
        try {
            this._windowMessageTransport.connect();
            buffer = await this._walletApiClient.message.sign(this._account.id, Buffer.from(message));
            this._windowMessageTransport.disconnect();
        }
        catch (err) {
            this._catchWalletApiError(err, "Something went wrong when signing a message with ledger live signer!");
        }
        return buffer.toString();
    }
    async signTransaction(transaction) {
        this._checkAccount();
        const ethereumTransaction = this._getWalletApiEthereumTransaction(transaction);
        let buffer;
        try {
            this._windowMessageTransport.connect();
            buffer = await this._walletApiClient.transaction.sign(this._account.id, ethereumTransaction);
            this._windowMessageTransport.disconnect();
        }
        catch (err) {
            this._catchWalletApiError(err, "Something went wrong when signing a transaction with ledger live signer!");
        }
        return buffer.toString();
    }
    async sendTransaction(transaction) {
        var _a;
        this._checkProviderAndAccount();
        const pupulatedTransaction = await this.populateTransaction(transaction);
        const ethereumTransaction = this._getWalletApiEthereumTransaction(pupulatedTransaction);
        let transactionHash;
        try {
            this._windowMessageTransport.connect();
            transactionHash =
                await this._walletApiClient.transaction.signAndBroadcast(this._account.id, ethereumTransaction);
            this._windowMessageTransport.disconnect();
        }
        catch (err) {
            this._catchWalletApiError(err, "Something went wrong when sending a transaction with ledger live signer!");
        }
        const transactionResponse = await ((_a = this.provider) === null || _a === void 0 ? void 0 : _a.getTransaction(transactionHash));
        if (!transactionResponse) {
            throw new Error("Transaction response not found!");
        }
        return transactionResponse;
    }
    connect(provider) {
        const account = this._account;
        const newLedgerLiveEthereumSignerInstance = new LedgerLiveEthereumSigner(provider);
        newLedgerLiveEthereumSignerInstance.setAccount(account);
        return newLedgerLiveEthereumSignerInstance;
    }
}
exports.LedgerLiveEthereumSigner = LedgerLiveEthereumSigner;
const getWindowMessageTransport = () => {
    return new wallet_api_client_1.WindowMessageTransport();
};
const getWalletAPIClient = (windowMessageTransport) => {
    const walletApiClient = new wallet_api_client_1.WalletAPIClient(windowMessageTransport);
    return walletApiClient;
};
//# sourceMappingURL=ledger.js.map